/**
 * Minimal PDF 1.4 builder: one JPEG image per page (DCTDecode).
 * No external deps — suitable for Fabric artboard exports.
 */

export interface PdfPageImage {
  jpeg: Uint8Array;
  width: number;
  height: number;
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

function enc(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

/**
 * Build a multi-page PDF from JPEG page images.
 * Each page's MediaBox matches the image pixel size (1 PDF unit = 1 px).
 */
export function pdfFromJpegs(pages: PdfPageImage[]): Blob {
  if (!pages.length) {
    throw new Error('No pages for PDF');
  }

  const parts: Uint8Array[] = [];
  const offsets: number[] = [0]; // 1-indexed object offsets; [0] unused
  let pos = 0;

  const push = (chunk: Uint8Array | string) => {
    const bytes = typeof chunk === 'string' ? enc(chunk) : chunk;
    parts.push(bytes);
    pos += bytes.length;
  };

  const startObj = (id: number) => {
    offsets[id] = pos;
  };

  push('%PDF-1.4\n');

  const catalogId = 1;
  const pagesId = 2;
  // Objects: catalog(1), pages(2), then per page: page, content, image
  const pageObjIds: number[] = [];
  let nextId = 3;

  for (let i = 0; i < pages.length; i += 1) {
    pageObjIds.push(nextId);
    nextId += 3; // page, contents, image
  }

  // 1 Catalog
  startObj(catalogId);
  push(`${catalogId} 0 obj\n<< /Type /Catalog /Pages ${pagesId} 0 R >>\nendobj\n`);

  // 2 Pages
  startObj(pagesId);
  const kids = pageObjIds.map((id) => `${id} 0 R`).join(' ');
  push(
    `${pagesId} 0 obj\n<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>\nendobj\n`
  );

  for (let i = 0; i < pages.length; i += 1) {
    const page = pages[i];
    const pageId = pageObjIds[i];
    const contentId = pageId + 1;
    const imageId = pageId + 2;
    const w = Math.max(1, Math.round(page.width));
    const h = Math.max(1, Math.round(page.height));

    const contentStream = `q\n${w} 0 0 ${h} 0 0 cm\n/Im0 Do\nQ\n`;
    const contentBytes = enc(contentStream);

    startObj(pageId);
    push(
      `${pageId} 0 obj\n` +
        `<< /Type /Page /Parent ${pagesId} 0 R ` +
        `/MediaBox [0 0 ${w} ${h}] ` +
        `/Contents ${contentId} 0 R ` +
        `/Resources << /XObject << /Im0 ${imageId} 0 R >> >> >>\n` +
        `endobj\n`
    );

    startObj(contentId);
    push(
      `${contentId} 0 obj\n<< /Length ${contentBytes.length} >>\nstream\n`
    );
    push(contentBytes);
    push(`\nendstream\nendobj\n`);

    startObj(imageId);
    push(
      `${imageId} 0 obj\n` +
        `<< /Type /XObject /Subtype /Image ` +
        `/Width ${w} /Height ${h} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 ` +
        `/Filter /DCTDecode /Length ${page.jpeg.length} >>\n` +
        `stream\n`
    );
    push(page.jpeg);
    push(`\nendstream\nendobj\n`);
  }

  const xrefStart = pos;
  const objCount = nextId; // highest id + 1
  push(`xref\n0 ${objCount}\n`);
  push(`0000000000 65535 f \n`);
  for (let id = 1; id < objCount; id += 1) {
    const off = offsets[id] ?? 0;
    push(`${String(off).padStart(10, '0')} 00000 n \n`);
  }
  push(
    `trailer\n<< /Size ${objCount} /Root ${catalogId} 0 R >>\n` +
      `startxref\n${xrefStart}\n%%EOF\n`
  );

  const bytes = concatBytes(parts);
  return new Blob([Uint8Array.from(bytes)], { type: 'application/pdf' });
}
